import React, { useState, useEffect, useCallback } from "react";
import {
  History,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  Search,
  ChevronRight,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { AutomationExecution, AutomationExecutionStep } from "./types";
import { fetchExecutions, fetchExecutionSteps } from "./automationsApi";

interface AutomationHistoryViewProps {
  initialAutomationId?: number;
  crmId?: string;
}

export const AutomationHistoryView: React.FC<AutomationHistoryViewProps> = ({
  initialAutomationId,
  crmId,
}) => {
  const [executions, setExecutions] = useState<AutomationExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const [statusFilter, setStatusFilter] = useState<string>("todos");
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Drawer de detalhes da execução
  const [selectedExecution, setSelectedExecution] =
    useState<AutomationExecution | null>(null);
  const [executionSteps, setExecutionSteps] = useState<
    AutomationExecutionStep[]
  >([]);
  const [loadingSteps, setLoadingSteps] = useState(false);

  const loadExecutions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchExecutions(
        { automacao_id: initialAutomationId, limit: 100 },
        crmId,
      );
      setExecutions(res.data || []);
    } catch (err: any) {
      setError(err.message || "Falha ao carregar histórico de execuções.");
    } finally {
      setLoading(false);
    }
  }, [initialAutomationId, crmId]);

  useEffect(() => {
    loadExecutions();
  }, [loadExecutions]);

  // Abrir trilha detalhada de uma execução
  const handleOpenDetail = async (exec: AutomationExecution) => {
    setSelectedExecution(exec);
    setLoadingSteps(true);
    try {
      const res = await fetchExecutionSteps(exec.id, crmId);
      setExecutionSteps(res.data || []);
    } catch (err) {
      console.error(err);
      setExecutionSteps([]);
    } finally {
      setLoadingSteps(false);
    }
  };

  // Filtragem local
  const filtered = executions.filter((e) => {
    if (statusFilter !== "todos" && e.status !== statusFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchName = e.automacoes?.nome?.toLowerCase().includes(term);
      const matchId = String(e.registro_id).toLowerCase().includes(term);
      const matchItem = e.item.toLowerCase().includes(term);
      if (!matchName && !matchId && !matchItem) return false;
    }
    return true;
  });

  return (
    <div className="space-y-4">
      {/* BARRA DE FILTROS */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-card/60 p-3.5 rounded-xl border border-border/80">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 absolute left-3 top-3 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome, lead ou item..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-9 text-xs bg-background"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-36 text-xs bg-background">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos Status</SelectItem>
              <SelectItem value="sucesso">Sucesso</SelectItem>
              <SelectItem value="falha">Falha</SelectItem>
              <SelectItem value="ignorada">Ignorada</SelectItem>
              <SelectItem value="em_execucao">Em Execução</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={loadExecutions}
          disabled={loading}
          className="h-9 text-xs gap-1.5 self-end sm:self-auto"
        >
          <RefreshCw
            className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
          />
          Atualizar
        </Button>
      </div>

      {/* TABELA DE EXECUÇÕES */}
      <div className="rounded-xl border border-border/80 bg-card overflow-hidden shadow-xs">
        {loading ? (
          <div className="py-16 text-center text-xs text-muted-foreground flex flex-col items-center gap-2">
            <RefreshCw className="w-5 h-5 animate-spin text-primary" />
            Carregando histórico de execuções...
          </div>
        ) : error ? (
          <div className="py-12 text-center text-xs text-rose-500">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-xs text-muted-foreground space-y-1">
            <History className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="font-semibold text-foreground">
              Nenhuma execução registrada.
            </p>
            <p className="text-[11px]">
              Quando suas automações ativas forem disparadas por eventos do CRM,
              o histórico aparecerá aqui.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-border/80 bg-muted/40 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Automação</th>
                  <th className="py-3 px-4">Gatilho</th>
                  <th className="py-3 px-4">Registro ID</th>
                  <th className="py-3 px-4">Origem</th>
                  <th className="py-3 px-4">Início</th>
                  <th className="py-3 px-4 text-right">Trilha</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {filtered.map((exec) => {
                  const dataInicio = new Date(exec.iniciada_em).toLocaleString(
                    "pt-BR",
                    {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    },
                  );

                  return (
                    <tr
                      key={exec.id}
                      onClick={() => handleOpenDetail(exec)}
                      className="hover:bg-muted/30 transition-colors cursor-pointer group"
                    >
                      {/* Status */}
                      <td className="py-3 px-4">
                        {exec.status === "sucesso" && (
                          <Badge
                            variant="outline"
                            className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 gap-1 text-[11px]"
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            Sucesso
                          </Badge>
                        )}
                        {exec.status === "falha" && (
                          <Badge
                            variant="outline"
                            className="bg-rose-500/10 text-rose-600 border-rose-500/30 gap-1 text-[11px]"
                          >
                            <XCircle className="w-3 h-3" />
                            Falha
                          </Badge>
                        )}
                        {exec.status === "ignorada" && (
                          <Badge
                            variant="outline"
                            className="bg-muted text-muted-foreground gap-1 text-[11px]"
                          >
                            <AlertTriangle className="w-3 h-3" />
                            Ignorada
                          </Badge>
                        )}
                        {exec.status === "em_execucao" && (
                          <Badge
                            variant="outline"
                            className="bg-blue-500/10 text-blue-600 border-blue-500/30 gap-1 text-[11px]"
                          >
                            <Clock className="w-3 h-3 animate-spin" />
                            Executando
                          </Badge>
                        )}
                      </td>

                      {/* Automação */}
                      <td className="py-3 px-4 font-semibold text-foreground">
                        {exec.automacoes?.nome ||
                          `Automação #${exec.automacao_id}`}
                      </td>

                      {/* Gatilho */}
                      <td className="py-3 px-4 capitalize text-muted-foreground">
                        {exec.item} • {exec.evento}
                      </td>

                      {/* Registro ID */}
                      <td className="py-3 px-4 font-mono text-[11px] text-muted-foreground">
                        {exec.registro_id}
                      </td>

                      {/* Origem */}
                      <td className="py-3 px-4">
                        <Badge variant="secondary" className="text-[10px]">
                          {exec.origem === "automacao"
                            ? "⚙️ Automação (RN-11)"
                            : "👤 Usuário"}
                        </Badge>
                      </td>

                      {/* Início */}
                      <td className="py-3 px-4 text-muted-foreground">
                        {dataInicio}
                      </td>

                      {/* Ver Trilha */}
                      <td className="py-3 px-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-[11px] text-primary group-hover:bg-primary/10 gap-1"
                        >
                          Ver Trilha
                          <ChevronRight className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── SHEET: TRILHA DE EXECUÇÃO DETALHADA (AUDITORIA) ──────────────────── */}
      <Sheet
        open={!!selectedExecution}
        onOpenChange={(o) => !o && setSelectedExecution(null)}
      >
        <SheetContent className="w-full sm:max-w-xl flex flex-col h-full overflow-y-auto gap-0">
          {selectedExecution && (
            <>
              <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/60">
                <div className="flex items-center gap-2 text-primary font-semibold text-xs tracking-wider uppercase">
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                  Auditoria de Execução #{selectedExecution.id}
                </div>
                <SheetTitle className="text-xl font-bold">
                  {selectedExecution.automacoes?.nome ||
                    `Automação #${selectedExecution.automacao_id}`}
                </SheetTitle>
                <SheetDescription className="text-xs text-muted-foreground">
                  Gatilho: {selectedExecution.item} {selectedExecution.evento} •
                  Registro {selectedExecution.registro_id}
                </SheetDescription>
              </SheetHeader>

              <div className="flex-1 px-6 py-5 space-y-6">
                {/* ERRO GERAL SE HOUVER */}
                {selectedExecution.erro && (
                  <div className="p-3.5 rounded-xl border border-rose-500/30 bg-rose-500/5 text-xs text-rose-600 dark:text-rose-400 space-y-1">
                    <span className="font-bold">Erro na execução:</span>
                    <p className="font-mono text-[11px]">
                      {selectedExecution.erro}
                    </p>
                  </div>
                )}

                {/* PASSOS DA EXECUÇÃO */}
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Trilha de Passos Executados
                  </h4>

                  {loadingSteps ? (
                    <div className="py-8 text-center text-xs text-muted-foreground">
                      Carregando passos...
                    </div>
                  ) : executionSteps.length === 0 ? (
                    <div className="p-4 rounded-xl border border-border/60 bg-muted/20 text-xs text-muted-foreground">
                      Nenhum passo detalhado registrado para esta execução.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {executionSteps.map((step, idx) => (
                        <div
                          key={step.id || idx}
                          className="p-3.5 rounded-xl border border-border/70 bg-card/60 space-y-2 text-xs"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-primary">
                                {step.step_key || `Passo ${idx + 1}`}
                              </span>
                              {step.ramo_id && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px]"
                                >
                                  Ramo: {step.ramo_id}
                                </Badge>
                              )}
                            </div>

                            <Badge
                              variant="outline"
                              className={`text-[10px] ${
                                step.status === "sucesso"
                                  ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
                                  : step.status === "falha"
                                    ? "bg-rose-500/10 text-rose-600 border-rose-500/30"
                                    : "bg-muted text-muted-foreground"
                              }`}
                            >
                              {step.status}
                            </Badge>
                          </div>

                          {/* Erro do passo */}
                          {step.erro && (
                            <p className="text-rose-500 text-[11px] font-mono">
                              Erro: {step.erro}
                            </p>
                          )}

                          {/* Resposta / Saída */}
                          {step.saida && Object.keys(step.saida).length > 0 && (
                            <div className="space-y-1">
                              <span className="text-[10px] text-muted-foreground font-semibold">
                                Saída / Resposta:
                              </span>
                              <pre className="p-2 rounded bg-muted/40 text-[10px] font-mono overflow-x-auto text-foreground">
                                {JSON.stringify(step.saida, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* SNAPSHOT DOS DADOS (RN-12) */}
                {selectedExecution.copia_dados && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">
                      Snapshot dos Dados do Registro (RN-12)
                    </h4>
                    <pre className="p-3 rounded-xl border border-border/60 bg-muted/30 text-[11px] font-mono overflow-x-auto text-foreground max-h-48">
                      {JSON.stringify(selectedExecution.copia_dados, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
